import { Box } from "@mui/material";
import type { UseMutationResult } from "@tanstack/react-query";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TranslationKeys } from "../../../i18n/types";
import type { WageRulesConfigResponse, WageSettingsResponse } from "../../../types/wageRules";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import { type DeepLinkRowMatcher, useWageRuleDeepLink, type WageRuleDeepLink } from "./useWageRuleDeepLink";
import { useWageRulesDraft, type WageRulesDraftSpec } from "./useWageRulesDraft";
import { WageSettingsSaveBar } from "./WageSettingsSaveBar";
import type { WageRulesTableProps } from "./wageRulesTableProps";

export type WageRulesSectionConfig<TRow extends { name: string }> = WageRulesDraftSpec<TRow> & {
	Table: FC<WageRulesTableProps<TRow>>;
	/** What the rules do, under the table. */
	explainerKey: TranslationKeys;
	/** Prefix for the section's own test ids (`-explainer`, `-save`, `-discard`, ...). */
	dataTestId: string;
	/** Which rows a deep link to this section highlights (`deepLinkRowMatchers.ts`). */
	matchDeepLinkRows: DeepLinkRowMatcher<TRow>;
};

/** What `WageSettings` hands each of the three rule sections. */
export type RuleSectionProps = {
	/** The tab's own read (`useWageSettings`). `undefined` while it loads, or when it failed. */
	settings: WageSettingsResponse | undefined;
	isReadOnly: boolean;
	/** Set when a deep link points at THIS section: it scrolls here and highlights the rows it names. */
	deepLink: WageRuleDeepLink;
};

type Props<TRow extends { name: string }> = RuleSectionProps & {
	config: WageRulesSectionConfig<TRow>;
	save: UseMutationResult<WageRulesConfigResponse<TRow>, Error, TRow[]>;
};

/**
 * DAT-905 · One wage rule table on Team → Wage Settings, with its own draft, Save and Discard.
 *
 * Saved separately from its neighbours on purpose: each table has its own endpoint, and a save of
 * one must never resend another's untouched rows.
 */
export const WageRulesSection = <TRow extends { name: string }>({ config, settings, save, isReadOnly, deepLink }: Props<TRow>) => {
	const { t } = useTranslation();
	const draft = useWageRulesDraft(config, settings, save);
	const { sectionRef, highlightedRowIndices, isTarget } = useWageRuleDeepLink(deepLink, draft.rowsLoaded, draft.rows, config.matchDeepLinkRows);
	const { Table } = config;

	const canSave = draft.rowsLoaded && draft.isDirty && draft.reasons.length === 0 && !isReadOnly && !draft.isPending;
	const disabledReason: ReactNode | undefined = isReadOnly ? (
		t("team.wageSettings.rules.readOnly")
	) : !draft.rowsLoaded ? (
		t("team.wageSettings.rules.notLoaded")
	) : draft.reasons.length > 0 ? (
		<>
			{t("team.wageSettings.rules.cannotSave")}
			<Box component="ul" sx={{ mt: 0.5, mb: 0, mx: 0, pl: 2.5 }}>
				{draft.reasons.map((reason) => (
					<li key={reason}>{t(reason)}</li>
				))}
			</Box>
		</>
	) : undefined;

	return (
		<Box
			component="section"
			ref={sectionRef}
			data-testid={config.dataTestId}
			data-deep-link-target={isTarget ? "true" : undefined}
			sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
		>
			<Table
				rows={draft.rows}
				setRows={draft.setRows}
				roles={draft.roles}
				jobIds={draft.jobIds}
				departmentId={draft.departmentId}
				// Also locked until the stored rows arrive: an edit made before then would stop the draft from
				// re-seeding, and saving it would replace every stored rule with the typed rows.
				disabled={isReadOnly || draft.isPending || !draft.rowsLoaded}
				highlightedRowIndices={highlightedRowIndices}
			/>

			<InlineHint tone="info" dataTestId={`${config.dataTestId}-explainer`} sx={{ width: "100%" }}>
				{t(config.explainerKey)}
			</InlineHint>

			<WageSettingsSaveBar
				isReadOnly={isReadOnly}
				readOnlyHint={t("team.wageSettings.rules.readOnly")}
				isDirty={draft.isDirty}
				canSave={canSave}
				isPending={draft.isPending}
				disabledReason={disabledReason}
				onDiscard={draft.discard}
				onSave={draft.requestSave}
				saveLabel={t("team.wageSettings.rules.save")}
				discardLabel={t("team.wageSettings.discard")}
				dataTestId={config.dataTestId}
			/>
		</Box>
	);
};
